import React, { createContext, useContext, useState, ReactNode } from "react";

export interface TourGuideContextType {
  activeTourModule: string | null;
  highlightedItemKey: string | null;
  setHighlightedItemKey: (key: string | null) => void;
  setActiveTourModule: (module: string | null) => void;
  currentTourStep: number;
  setCurrentTourStep: (step: number) => void;
  hoverTooltipsEnabled: boolean;
  setHoverTooltipsEnabled: (enabled: boolean) => void;
  hoveredStepId: string | null;
  setHoveredStepId: (stepId: string | null) => void;
  disabledHoverSteps: Set<string>;
  setDisabledHoverSteps: (steps: Set<string>) => void;
}

const TourGuideContext = createContext<TourGuideContextType | undefined>(
  undefined
);

interface TourGuideProviderProps {
  children: ReactNode;
}

export const TourGuideProvider: React.FC<TourGuideProviderProps> = ({
  children,
}) => {
  const [activeTourModule, setActiveTourModule] = useState<string | null>(null);
  const [highlightedItemKey, setHighlightedItemKey] = useState<string | null>(
    null
  );
  const [currentTourStep, setCurrentTourStep] = useState<number>(0);
  const [hoverTooltipsEnabled, setHoverTooltipsEnabled] =
    useState<boolean>(true);
  const [hoveredStepId, setHoveredStepId] = useState<string | null>(null);
  const [disabledHoverSteps, setDisabledHoverSteps] = useState<Set<string>>(
    new Set()
  );

  const value: TourGuideContextType = {
    activeTourModule,
    highlightedItemKey,
    setHighlightedItemKey,
    setActiveTourModule,
    currentTourStep,
    setCurrentTourStep,
    hoverTooltipsEnabled,
    setHoverTooltipsEnabled,
    hoveredStepId,
    setHoveredStepId,
    disabledHoverSteps,
    setDisabledHoverSteps,
  };

  return (
    <TourGuideContext.Provider value={value}>
      {children}
    </TourGuideContext.Provider>
  );
};

export const useTourGuideContext = (): TourGuideContextType => {
  const context = useContext(TourGuideContext);
  if (context === undefined) {
    throw new Error(
      "useTourGuideContext must be used within a TourGuideProvider"
    );
  }
  return context;
};
